import React, { ReactElement } from 'react';

interface ScoreBlockProps {
    label: string;
    icon: string;
    score?: number | null;
    unavailableReason?: string | null;
    // Direction of the scale. Health is higher-is-better; Risk is a magnitude (1 low, 10 high)
    // and passes `false` so the color bands invert with it.
    higherIsBetter?: boolean;
}

export type ScoreBand = 'good' | 'warn' | 'bad';

// Both scales run 1-10, so a descending metric maps onto the ascending bands via `10 - score`.
// Exported because the breakdown table colours its score badges by the same bands — the
// thresholds must be decided in exactly one place or a row and its group can disagree.
export const scoreBand = (score: number, higherIsBetter: boolean): ScoreBand => {
    const normalized = higherIsBetter ? score : 10 - score;
    if (normalized >= 8.5) return 'good';
    if (normalized > 3.5) return 'warn';
    return 'bad';
};

const ScoreBlock = ({ label, icon, score, unavailableReason, higherIsBetter = true }: ScoreBlockProps): ReactElement => {
    // The locations payload rides in through a passthrough schema and a cast, so guard the
    // value rather than trusting the prop type — a non-numeric score would throw on toFixed
    // and take the whole Overview tab down with the error boundary.
    const numericScore = typeof score === 'number' && Number.isFinite(score) ? score : null;
    const hasScore = numericScore !== null;

    return (
        <div
            className={`lst-score-block ${hasScore ? `lst-score-block--${scoreBand(numericScore, higherIsBetter)}` : 'lst-score-block--placeholder'}`}
            title={hasScore ? undefined : unavailableReason ?? undefined}
        >
            <div className='lst-score-block__header'>
                <i className={`${icon} lst-score-block__icon`} />
                <span className='lst-score-block__label'>{label}</span>
            </div>
            <span className='lst-score-block__value'>{hasScore ? numericScore.toFixed(1) : '—'}</span>
        </div>
    );
};

export default ScoreBlock;
